/**
 * The recipe of a house-made ingredient (#180): what goes into a batch, a
 * syrup, a juice; what it yields; how long it keeps. Saving writes the
 * ingredient's build, and the API re-costs it and everything it sits
 * inside, so the cost shown here is the one every recipe pouring it pays.
 */

import { useMemo, useState } from "react";
import {
  batchCosts,
  batchProblem,
  buildVolumeMl,
  MAX_BATCH_YIELD_ML,
  MAX_SHELF_LIFE_DAYS,
  slugify,
  type HouseMadeBuild,
  type Ingredient,
  type SpecLine,
} from "@baros/core";
import { api } from "../api.js";
import { useApp } from "../context.js";
import { money, pct } from "../format.js";

interface Props {
  /** The ingredient being edited, or undefined to make a new one. */
  readonly ingredient: Ingredient | undefined;
  readonly onClose: () => void;
  readonly onSaved: (ingredient: Ingredient) => void;
}

const EMPTY_BUILD: HouseMadeBuild = { lines: [], yieldMl: null, shelfLifeDays: null };

export function HouseMadeEditor(p: Props) {
  const app = useApp();
  const [name, setName] = useState(p.ingredient?.name ?? "");
  const [build, setBuild] = useState<HouseMadeBuild>(() => p.ingredient?.build ?? EMPTY_BUILD);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ingredients = useMemo(() => new Map(app.library.ingredients.map((i) => [i.ingredientId, i])), [app.library.ingredients]);
  const ownId = p.ingredient?.ingredientId ?? (name.trim() ? slugify(name) : "");
  // What can go in: anything but itself.
  const choices = useMemo(
    () => app.library.ingredients.filter((i) => i.ingredientId !== ownId).sort((a, b) => a.name.localeCompare(b.name)),
    [app.library.ingredients, ownId],
  );

  // The cost and strength the build gives, worked the way the API will write them.
  const figures = useMemo(() => {
    if (build.lines.length === 0) return null;
    const byId = new Map<string, Pick<Ingredient, "costPerMl" | "abv" | "build">>(ingredients);
    byId.set(ownId || "__draft", { costPerMl: 0, abv: 0, build });
    return batchCosts(byId).get(ownId || "__draft") ?? null;
  }, [build, ingredients, ownId]);
  const volume = buildVolumeMl(build.lines);
  const loop = ownId ? batchProblem(ownId, build.lines, ingredients) : null;

  function setLine(i: number, part: Partial<SpecLine>) {
    setBuild((b) => ({ ...b, lines: b.lines.map((l, j) => (j === i ? { ...l, ...part } : l)) }));
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      setErr("Give it a name");
      return;
    }
    if (loop) {
      setErr(loop);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const existing = p.ingredient;
      const body = existing
        ? { ...existing, name: trimmed, build }
        : { name: trimmed, costPerMl: 0, abv: 0, aromaFamily: null, kind: "other", source: "manual", build };
      const { ingredient } = await api.saveIngredient(app.token, ownId, body);
      await app.refreshLibrary();
      p.onSaved(ingredient);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="batch-make" style={{ gridColumn: "1 / -1" }}>
      <div className="grid2" style={{ maxWidth: 640 }}>
        <label className="field"><span>Name</span>
          <input type="text" value={name} autoFocus={!p.ingredient} onChange={(e) => setName(e.target.value)} placeholder="Negroni batch, Honey syrup…" />
        </label>
        <label className="field"><span>A batch makes (mL, optional)</span>
          <input className="num-input" type="number" min={0} max={MAX_BATCH_YIELD_ML} step={50} value={build.yieldMl ?? ""} onChange={(e) => setBuild({ ...build, yieldMl: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className="field"><span>Keeps for (days, optional)</span>
          <input className="num-input" type="number" min={0} max={MAX_SHELF_LIFE_DAYS} step={1} value={build.shelfLifeDays ?? ""} onChange={(e) => setBuild({ ...build, shelfLifeDays: e.target.value ? Number(e.target.value) : null })} />
        </label>
      </div>

      <div>
        <div className="label" style={{ marginBottom: 8 }}>
          What goes in · {Math.round(volume)} mL
          {figures ? ` · ${money(figures.costPerMl * 1000)} a litre · ${pct(figures.abv)} ABV` : ""}
        </div>
        {build.lines.map((l, i) => (
          <div className="line-row" key={i}>
            <span className="swatch" style={{ background: ingredients.get(l.ingredientId)?.build ? "var(--ac)" : "var(--ln)" }} />
            <select value={l.ingredientId} onChange={(e) => setLine(i, { ingredientId: e.target.value })}>
              {!ingredients.has(l.ingredientId) && <option value={l.ingredientId}>{l.ingredientId} (unknown)</option>}
              {choices.map((o) => <option key={o.ingredientId} value={o.ingredientId}>{o.name}{o.build ? " (house-made)" : ""}</option>)}
            </select>
            {/* The batch's own ceiling, as the yield above: a build line is
                part of a batch, not a measure poured into a glass. */}
            <input className="num-input" type="number" min={0} max={MAX_BATCH_YIELD_ML} step={5} value={l.ml} onChange={(e) => setLine(i, { ml: Number(e.target.value) })} />
            <span className="label">mL</span>
            <button className="btn sm danger" style={{ padding: "0 6px" }} onClick={() => setBuild({ ...build, lines: build.lines.filter((_, j) => j !== i) })} aria-label="Remove">×</button>
          </div>
        ))}
        <button
          className="btn dashed"
          style={{ marginTop: 8 }}
          onClick={() => choices[0] && setBuild({ ...build, lines: [...build.lines, { ingredientId: choices[0].ingredientId, ml: 100, unit: "ml" }] })}
        >
          + Add line
        </button>
        {build.lines.length === 0 && (
          <div className="label" style={{ marginTop: 8, lineHeight: 1.6 }}>
            Without lines it is house-made in name only: counted, made and poured, but costed by hand on the ingredient.
          </div>
        )}
      </div>

      {(err || loop) && <div className="msg err">{err ?? loop}</div>}
      <div className="row" style={{ gap: 10 }}>
        <button className="btn" onClick={p.onClose} disabled={saving}>Cancel</button>
        <button className="btn primary" onClick={() => void save()} disabled={saving || !name.trim()}>
          {saving ? "Saving…" : p.ingredient ? "Save recipe" : "Add house-made ingredient"}
        </button>
      </div>
    </div>
  );
}
